/**
 * Minelab × Race to the Realm — Brand Config
 *
 * Sibling of CustomHubBrandConfig in ../registry.ts. Collects all the
 * RTTR-specific bindings (slug, loader component, lazy hub component,
 * font preload list, shell behavior) into one object that the registry
 * imports.
 *
 * Once Sprints 4-5 land, the touch points that currently hardcode
 * "minelab-rttr" by string (main.tsx path fork, HubPageRouter
 * SLUG_LOADERS, index.html brand-isolation script) will all read from
 * this config via the registry. Adding a second campaign (Polaris × CoD,
 * Sprint 7) is then a parallel sibling module here plus a one-line
 * registry import.
 */

import React, { lazy } from "react";
import type { CustomHubProps } from "@/pages/hub/custom/types";
import type { CustomHubBrandConfig } from "../registry";
import { MinelabRttrLoadingFallback } from "@/pages/hub/custom/minelab-rttr/components/MinelabRttrLoadingFallback";

/**
 * Industry font weights self-hosted under /fonts/Industry/. Order
 * mirrors typical use frequency (body weight first, display weight last)
 * — earlier preloads start downloading sooner under HTTP/2 multiplexing.
 *
 * Keep this in sync with the @font-face declarations in
 * MinelabRttrHub.scss. If the SCSS adds a new weight or renames a file,
 * update both — there's no compile-time link between the two today.
 */
const RTTR_FONT_PRELOAD_HREFS = [
    "/fonts/Industry/industry-lbook.otf",
    "/fonts/Industry/industry-lmedium.otf",
    "/fonts/Industry/industry-ldemi.otf",
    "/fonts/Industry/industry-lbold.otf",
    "/fonts/Industry/industry-lblack.otf",
] as const;

/*
 * Lazy import of the hub component. Mirrors the existing definition in
 * HubPageRouter.tsx so the chunk-graph shape stays identical until Sprint
 * 6 (manualChunks) consciously moves it.
 */
const MinelabRttrHubLazy: React.LazyExoticComponent<React.FC<CustomHubProps>> = lazy(() =>
    import("@/pages/hub/custom/minelab-rttr/MinelabRttrHub"));

export const MINELAB_RTTR_HUB_CONFIG: CustomHubBrandConfig = {
    slug: "minelab-rttr",
    loaderComponent: MinelabRttrLoadingFallback,
    lazyComponent: MinelabRttrHubLazy,
    fontPreloadHrefs: RTTR_FONT_PRELOAD_HREFS,
    /*
     * Industry fonts are .otf files (OpenType). Preloads MUST set
     * type="font/otf" or Chromium-based browsers reject the preload.
     */
    fontPreloadType: "font/otf",

    /* Mounts MinelabRttrShipModal inside MinelabRttrHub for MERCH_FULFILLMENT. */
    rendersOwnRewardNotifications: true,
};
